import { Col, Form, Row } from "react-bootstrap";
import QueryForm from "../../../../../modules/amc/components/QueryForm";
import { useMemo, useState } from "react";
import QueryCard from "../../../../../modules/amc/components/ReportTypeCard";
import BreadcrumbDates from "../../../../../components/core/blocks/BreadcrumbDates";
import { AmcInstanceDetails } from "../../../../../modules/amc/components/instance/AmcInstanceDetails";
import { DASHBOARD_LINKS } from "../../../../../utils/dashboardLinks";
import {
  AMC_QUERY_EXPLORER_BREADCRUMB_SPEC,
  CANNED_QUERIES,
  FEATURED_REPORTS,
  normalizeQueryName,
} from "../../../../../modules/amc/utils/utils";
import Pagination from "../../../../../modules/amc/components/Pagination";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { successToast, errorToast } from "../../../../../utils/toasts";
import api from "../../../../../utils/api";
import Loading from "../../../../../components/core/blocks/Loading";
import { useAMCQueryData } from "../../../../../modules/amc/hooks/useAMCQueryData";
import { useEnabledModules, useModuleEnabled } from "../../../../../redux/selectors/modules";
import { Navigate, createFileRoute } from "@tanstack/react-router";
import { Button } from "../../../../../components/core/basic/Button";
import { useAMCInstanceDataSouces } from "../../../../../modules/amc/hooks/useAMCInstanceDataSources";
import useQueryParams from "../../../../../hooks/useQueryParams";
import { Tabs, TabList, Tab, TabPanel } from "../../../../../components/core/basic/Tabs";
import { AMCSubscriptionsTable, filterToReportType } from "../../../../../modules/amc/components/AMCSubscriptionsTable";
import { Modal } from "../../../../../components/core/basic/Modal";
import { css } from "../../../../../../styled-system/css";
import { useSimpleCan } from "../../../../../config/casl/casl";
import { Box } from "../../../../../../styled-system/jsx";
import { useTranslation } from "react-i18next";

const PAGE_SIZE = 4;

const centeredPanelStyles = css({
  maxWidth: { base: "100%", lg: "66.67%" },
  marginX: "auto",
  paddingTop: "medium",
});

const AMCDashboard = () => {
  const [showQueryForm, setShowQueryForm] = useState(false);
  const [showCustomReportForm, setShowCustomReportForm] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [subscribedQuerySearch, setSubscribedQuerySearch] = useState("");
  const [availableQuerySearch, setAvailableQuerySearch] = useState("");
  const [currentSubscribedPage, setCurrentSubscribedPage] = useState(1);
  const [currentAvailablePage, setCurrentAvailablePage] = useState(1);
  const [querySection, setQuerySection] = useQueryParams("section", "all");
  const [subscriptionFilters, setSubscriptionFilters] = useState([]);

  const queryClient = useQueryClient();
  const hasAMCModule = useModuleEnabled("AMC");
  const enabledModules = useEnabledModules();
  const userCanCreateAmcSubscriptions = useSimpleCan("create", "AMC");
  const userCanUpdateAmcSubscriptions = useSimpleCan("update", "AMC");
  const { t } = useTranslation(["amc"]);

  const { data: subscribedQueriesData, isLoading } = useAMCQueryData();
  const { data: availableDataSources } = useAMCInstanceDataSouces();

  const subscribeHandler = useMutation({
    mutationFn: async (queryData) => {
      const { name, frequency, parameter_list } = queryData;

      const parameters = {};

      for (const param of parameter_list) {
        if (param in queryData) {
          parameters[param] = queryData[param];
        }
      }

      if (selectedQuery.subscribed) {
        const updateData = { frequency: frequency, parameters: parameters, query_type: name };

        await api.patch(`/amc/subscriptions/${selectedQuery.id}/`, updateData);
      } else {
        const subscriptionData = {
          enabled: true,
          query_type: name,
          parameters: parameters,
          frequency: frequency,
        };
        await api.post("/amc/subscriptions/", subscriptionData);
      }
    },

    select: (res) => res.data,
    enabled: true,
    default: [],

    onSuccess: () => {
      successToast({
        message:
          selectedQuery?.subscribed ?
            t(($) => $.amc["Successfully updated query parameters"])
          : t(($) => $.amc["Successfully subscribed to query"]),
      });
      setSelectedQuery(null);
      queryClient.invalidateQueries({
        queryKey: ["subscribedAMCQueries"],
      });
    },

    onError: (error) => {
      errorToast({
        message: t(($) => $.amc["Failed to subscribe to query"]),
        error,
      });
    },
  });

  const unsubscribeHandler = useMutation({
    mutationFn: async (subscriptionId) => {
      await api.remove(`/amc/subscriptions/${subscriptionId}`);
    },

    select: (res) => res.data,
    enabled: true,
    default: [],

    onSuccess: () => {
      successToast({
        message: t(($) => $.amc["Successfully unsubscribed from query"]),
      });
      queryClient.invalidateQueries({
        queryKey: ["subscribedAMCQueries"],
      });
    },

    onError: (error) => {
      errorToast({
        message: t(($) => $.amc["Failed to unsubscribe from query"]),
        error,
      });
    },
  });

  const toggleStatusHandler = useMutation({
    mutationFn: async (queryStatusData) => {
      const { subscriptionId, enabled } = queryStatusData;

      await api.patch(`/amc/subscriptions/${subscriptionId}/`, {
        enabled,
      });
    },

    select: (res) => res.data,
    enabled: true,
    default: [],

    onSuccess: (data, variables) => {
      successToast({
        message:
          variables.enabled ?
            t(($) => $.amc["Successfully enabled {queryName} query subscription"], { queryName: variables.queryName })
          : t(($) => $.amc["Successfully disabled {queryName} query subscription"], { queryName: variables.queryName }),
      });
      queryClient.invalidateQueries({
        queryKey: ["subscribedAMCQueries"],
      });
    },

    onError: (error, variables) => {
      const querySpec = CANNED_QUERIES.find((query) => {
        return query.name === variables?.queryName;
      });
      const displayName = querySpec?.display_name ? t(querySpec.display_name) : undefined;

      errorToast({
        message:
          variables.enabled ?
            t(($) => $.amc["Failed to enable {displayName} query"], { displayName })
          : t(($) => $.amc["Failed to disable {displayName} query"], { displayName }),
        error,
      });
    },
  });

  const breadCrumbItems = [
    {
      name: t(($) => $.amc["AMC Dashboard"]),
      href: "/user/amc/dashboard",
      unsorted: true,
      dropdownLinkOptions: DASHBOARD_LINKS,
      dropdownSubtitle: t(($) => $.amc["Reports"]),
    },
  ];

  if (!breadCrumbItems[0].dropdownLinkOptions?.find((o) => o.name === AMC_QUERY_EXPLORER_BREADCRUMB_SPEC.name)) {
    breadCrumbItems[0].dropdownLinkOptions.push(AMC_QUERY_EXPLORER_BREADCRUMB_SPEC);
  }

  const subscribedQueries = useMemo(() => {
    return (
      [...CANNED_QUERIES, ...FEATURED_REPORTS].filter((query) =>
        subscribedQueriesData?.find((subscribedQuery) => normalizeQueryName(subscribedQuery.query_type) === query.name),
      ) ?? []
    ).map((query) => {
      const querySubscriptionSettings = subscribedQueriesData?.find(
        (subscribedQuery) => normalizeQueryName(subscribedQuery.query_type) === query.name,
      );

      return { ...query, ...querySubscriptionSettings, subscribed: true };
    });
  }, [subscribedQueriesData]);

  const availableQueries = useMemo(() => {
    return (
      CANNED_QUERIES.filter((query) => {
        const { modules } = query;
        if (modules) {
          const module = enabledModules.find((module) => modules.includes(module.name));
          if (!module) {
            return false;
          }
        }
        return !subscribedQueriesData?.find(
          (subscribedQuery) => normalizeQueryName(subscribedQuery.query_type) === query.name,
        );
      }) ?? []
    );
  }, [subscribedQueriesData, enabledModules]);

  const filteredSubscribedQueries = useMemo(() => {
    return subscribedQueries?.filter((query) => {
      if (subscribedQuerySearch) {
        const { name, description } = query;
        const descText = typeof description === "function" ? t(description) : description;
        return `${name} ${descText}`.toLowerCase().includes(subscribedQuerySearch.toLowerCase());
      }
      return true;
    });
  }, [subscribedQuerySearch, subscribedQueries, t]);

  const filteredAvailableQueries = useMemo(() => {
    return availableQueries?.filter((query) => {
      if (availableQuerySearch) {
        const { name, description } = query;
        const descText = typeof description === "function" ? t(description) : description;
        return `${name} ${descText}`.toLowerCase().includes(availableQuerySearch.toLowerCase());
      }
      return true;
    });
  }, [availableQuerySearch, availableQueries, t]);

  const paginatedSubscribedQueries = useMemo(() => {
    return filteredSubscribedQueries?.slice(
      (currentSubscribedPage - 1) * PAGE_SIZE,
      (currentSubscribedPage - 1) * PAGE_SIZE + PAGE_SIZE,
    );
  }, [filteredSubscribedQueries, currentSubscribedPage]);

  const paginatedAvailableQueries = useMemo(() => {
    return filteredAvailableQueries.slice(
      (currentAvailablePage - 1) * PAGE_SIZE,
      (currentAvailablePage - 1) * PAGE_SIZE + PAGE_SIZE,
    );
  }, [filteredAvailableQueries, currentAvailablePage]);

  if (!hasAMCModule) {
    return <Navigate to="/user/dashboard" replace />;
  }

  return (
    <div style={{ minHeight: "calc(100vh - 150px" }}>
      <Row>
        <Col xs={12} className="mb-0">
          <BreadcrumbDates items={breadCrumbItems} hideDateSelector={true}>
            <AmcInstanceDetails />
          </BreadcrumbDates>
        </Col>
      </Row>
      <Row style={{ padding: "20px 150px" }}>
        <Col xs={12}>
          <h1 className="text-center" style={{ fontWeight: 600, fontSize: "24px" }}>
            {t(($) => $.amc["Amazon Marketing Cloud"])}
          </h1>
        </Col>
      </Row>
      {isLoading ?
        <Loading fullPage={true} />
      : <>
          <Tabs selectedKey={querySection} onSelectionChange={setQuerySection}>
            <Box maxWidth={{ base: "100%", lg: "66.67%" }} marginX="auto">
              <TabList>
                <Tab id="all">{t(($) => $.amc["Reports"])}</Tab>
                <Tab id="subscriptions">{t(($) => $.amc["Subscriptions"])}</Tab>
              </TabList>
            </Box>
            <TabPanel id="all" className={centeredPanelStyles}>
              <h3 className="m-0 py-2 px-5 fs-2" style={{ fontWeight: 600 }}>
                {t(($) => $.amc["Featured Reports"], { count: FEATURED_REPORTS.length })}
              </h3>
              <Row>
                {FEATURED_REPORTS.map((query, index) => {
                  return (
                    <QueryCard
                      query={query}
                      key={index}
                      subscribed={false}
                      featured={true}
                      availableSources={availableDataSources}
                      onViewSubscriptions={() => {
                        if (["PATH_TO_PURCHASE_GROUPS_INTERNAL"].includes(query.name)) {
                          setSubscriptionFilters(filterToReportType(query.name));
                          setQuerySection("subscriptions");
                        }
                      }}
                      onSelect={() => {
                        if (["PATH_TO_PURCHASE_GROUPS_INTERNAL"].includes(query.name)) {
                          setSelectedQuery({ ...query, subscribed: false, parameters: {} });
                          setShowQueryForm(true);
                        }
                      }}
                    />
                  );
                })}
              </Row>
            </TabPanel>
            <TabPanel id="subscriptions" className={centeredPanelStyles}>
              <AMCSubscriptionsTable
                columnFilters={subscriptionFilters}
                onColumnFiltersChange={setSubscriptionFilters}
                setSelectedQuery={setSelectedQuery}
                setShowQueryForm={setShowQueryForm}
                unsubscribeHandler={unsubscribeHandler}
                toggleStatusHandler={toggleStatusHandler}
              />
            </TabPanel>
          </Tabs>
          {querySection === "all" && (
            <Row>
              <Col xs={12} lg={{ span: 8, offset: 2 }} className="pb-5">
                <h3 className="m-0 py-2 px-5 fs-2" style={{ fontWeight: 600 }}>
                  {t(($) => $.amc["Subscribed Reports"], { count: subscribedQueries.length })}
                </h3>
                <Row>
                  <Col xs={12} md={6} xl={4} className="py-3 px-5">
                    <Form.Control
                      type="search"
                      placeholder={t(($) => $.amc["Search for a subscribed report..."])}
                      onChange={(e) => setSubscribedQuerySearch(e.target.value)}
                      style={{ borderRadius: "5px" }}
                    />
                  </Col>
                  {filteredSubscribedQueries.length > PAGE_SIZE && (
                    <Col xs={12} md={6} xl={8} className={"d-flex justify-content-end"}>
                      <Pagination
                        data={filteredSubscribedQueries}
                        pageSize={PAGE_SIZE}
                        currentPage={currentSubscribedPage}
                        setCurrentPage={setCurrentSubscribedPage}
                      />
                    </Col>
                  )}
                </Row>
                <Row>
                  {paginatedSubscribedQueries.map((query, index) => {
                    return (
                      <QueryCard
                        query={query}
                        key={index}
                        onSelect={() => {
                          setSelectedQuery({ ...query, subscribed: false, parameters: {} });
                          setShowQueryForm(true);
                        }}
                        onViewSubscriptions={() => {
                          setSubscriptionFilters(filterToReportType(query.name));
                          setQuerySection("subscriptions");
                        }}
                        subscribed={true}
                        availableSources={availableDataSources}
                      />
                    );
                  })}
                </Row>
              </Col>

              <Col xs={12} lg={{ span: 8, offset: 2 }}>
                <div className="d-flex justify-content-between">
                  <h3 className="m-0 py-2 ps-5 fs-2" style={{ fontWeight: 600 }}>
                    {t(($) => $.amc["Available Reports"], { count: availableQueries.length })}
                  </h3>
                  <Modal
                    isOpen={showCustomReportForm}
                    onOpenChange={(isOpen) => setShowCustomReportForm(isOpen)}
                    title={t(($) => $.amc["Request a Custom Report"])}
                    css={{
                      padding: "xlarge",
                      width: "60vw",
                    }}
                  >
                    <Row className="pb-5 d-flex justify-content-center gap-3">
                      <p className="text-center">
                        {t(
                          ($) =>
                            $.amc[
                              "To have a custom report created, please contact your customer success representative and provide a description of the custom report you would like to have generated."
                            ],
                        )}
                      </p>
                      <Button onPress={() => setShowCustomReportForm(false)}>{t(($) => $.amc["Continue"])}</Button>
                    </Row>
                  </Modal>
                  <Button onPress={() => setShowCustomReportForm(true)}>
                    {t(($) => $.amc["Request a Custom Report"])}
                  </Button>
                </div>
                <Row>
                  <Col xs={12} md={6} xl={4} className="py-3 px-5">
                    <Form.Control
                      type="search"
                      placeholder={t(($) => $.amc["Search for an available report..."])}
                      onChange={(e) => {
                        setAvailableQuerySearch(e.target.value);
                      }}
                      style={{ borderRadius: "5px" }}
                    />
                  </Col>
                  {filteredAvailableQueries.length > PAGE_SIZE && (
                    <Col xs={12} md={6} xl={8} className={"d-flex justify-content-end"}>
                      <Pagination
                        data={filteredAvailableQueries}
                        pageSize={PAGE_SIZE}
                        currentPage={currentAvailablePage}
                        setCurrentPage={setCurrentAvailablePage}
                      />
                    </Col>
                  )}
                </Row>
                <Row>
                  {paginatedAvailableQueries.map((query, index) => {
                    return (
                      <QueryCard
                        query={query}
                        key={index}
                        onSelect={() => {
                          setSelectedQuery(query);
                          setShowQueryForm(true);
                        }}
                        subscribed={false}
                        availableSources={availableDataSources}
                      />
                    );
                  })}
                </Row>
              </Col>
            </Row>
          )}
        </>
      }
      <Modal
        isOpen={showQueryForm}
        onOpenChange={(isOpen) => setShowQueryForm(isOpen)}
        title={(() => {
          const dn = selectedQuery?.display_name;
          const displayName = typeof dn === "function" ? t(dn) : dn;
          return selectedQuery?.subscribed ?
              t(($) => $.amc["Update {displayName} Query"], { displayName })
            : t(($) => $.amc["Subscribe to {displayName} Query"], { displayName });
        })()}
        css={{
          padding: "xlarge",
          width: "60vw",
        }}
      >
        {" "}
        <QueryForm
          setShowModal={(value) => setShowQueryForm(value)}
          query={selectedQuery}
          canCreate={userCanCreateAmcSubscriptions}
          canUpdate={userCanUpdateAmcSubscriptions}
          onFormSubmit={(query) => {
            const isUpdate = !!selectedQuery?.subscribed;
            if ((isUpdate && !userCanUpdateAmcSubscriptions) || (!isUpdate && !userCanCreateAmcSubscriptions)) {
              return;
            }
            subscribeHandler.mutate({ ...selectedQuery, ...query });
          }}
        />
      </Modal>
    </div>
  );
};

export const Route = createFileRoute("/_authenticated/user/amc/dashboard/")({
  component: AMCDashboard,
  staticData: { application: "Advertising" },
});
